import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('fleet_token'));
    const [loading, setLoading] = useState(true);
    const [companyEdition, setCompanyEdition] = useState(null);

    useEffect(() => {
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            fetchUser();
        } else {
            setLoading(false);
        }
    }, [token]);

    const fetchUser = async () => {
        try {
            const response = await axios.get(`${API_URL}/auth/me`);
            setUser(response.data);
            
            // Fetch company details to get edition
            try {
                const companyRes = await axios.get(`${API_URL}/auth/company`);
                setCompanyEdition(companyRes.data.edition || 'carrier');
            } catch (err) {
                console.error('Failed to fetch company:', err);
                setCompanyEdition('carrier'); // Default
            }
        } catch (error) {
            console.error('Failed to fetch user:', error);
            logout();
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password) => {
        const response = await axios.post(`${API_URL}/auth/login`, { email, password });
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('fleet_token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);
        setUser(userData);
        
        // Fetch company edition after login
        try {
            const companyRes = await axios.get(`${API_URL}/auth/company`);
            setCompanyEdition(companyRes.data.edition || 'carrier');
        } catch (err) {
            console.error('Failed to fetch company:', err);
            setCompanyEdition('carrier');
        }
        
        return userData;
    };

    const register = async (data) => {
        const response = await axios.post(`${API_URL}/auth/register`, data);
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('fleet_token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);
        setUser(userData);
        
        // Set edition from registration data
        setCompanyEdition(data.edition || 'carrier');
        
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('fleet_token');
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
        setCompanyEdition(null);
    };

    const isAdmin = () => user?.role === 'admin';
    const refreshUser = async () => {
        const response = await axios.get(`${API_URL}/auth/me`);
        setUser(response.data);
        return response.data;
    };
    const isFleetManager = () => user?.role === 'fleet_manager';
    const isDriver = () => user?.role === 'driver';
    const canManage = () => user?.role === 'admin' || user?.role === 'fleet_manager';

    return (
        <AuthContext.Provider value={{
            user,
            token,
            loading,
            companyEdition,
            login,
            register,
            logout,
            refreshUser,
            isAdmin,
            isFleetManager,
            isDriver,
            canManage
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
